import { CustomJoyrideTour } from 'components/facets/CustomJoyrideTour'
import { useUser } from 'components/providers/UserProvider'
import { useFetchPurchaseEvents } from 'lib/hooks/useFetchPurchaseEvents'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import {
  BASE_TAB_ID,
  MAX_LISTING_TABS,
  useListingTabsWithStocks,
} from 'lib/hooks/useListingTabsWithStocks'
import { useListingTour } from 'lib/hooks/useListingTour'
import { usePersistentStock } from 'lib/hooks/usePersistentStock'
import { FetchedStockInterface, useStockStore } from 'lib/hooks/useStockStore'
import { useTaxationStore } from 'lib/hooks/useTaxationStore'
import { areArticlesMatching } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  InventoryArticle,
  INVENTORY_ZOOM_LEVELS,
  ListingTab,
  PurchaseEvent,
  STOCK_TYPES,
} from 'shared'
import { ListingTabId, ListingTabMeta, LISTING_AND_APPRAISAL } from 'utils/constants'
import { SaveToInventoryDialog } from '../addArticles/SaveToInventoryDialog'
import { EditArticleContext } from '../addArticles/useEditArticleContext'
import { InventoryPresenter } from '../stockPricing'
import { EditArticleModal } from './EditArticleModal'
export const ListingAndAppraisalPresenter = (): JSX.Element => {
  const { user, setUser } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [editArticleModalOpen, setEditArticleModalOpen] = useState(false)
  const [saveToInventoryDialogOpen, setSaveToInventoryDialogOpen] = useState(false)

  const [articleToEdit, setArticleToEdit] = useState<InventoryArticle | null>(null)
  const [editAfterAdding, setEditAfterAdding] = useState(false)
  const [newArticle, setNewArticle] = useState(false)
  const { setZoomLevel, setSort } = useInventoryStore()
  const activeStockType = useStockStore((state) => state.activeStockType)

  const fetchedStock = useStockStore((s) => s.fetchedStock)
  const setFetchedStock = useStockStore((s) => s.setFetchedStock)

  const purchaseEvents = useTaxationStore((s) => s.purchaseEvents)
  const { fetchPurchaseEvents, loading: loadingLots } = useFetchPurchaseEvents()

  const [persistentStock, , loadingPersistent] = usePersistentStock({
    stock: [],
    sealedStock: [],
    fetchedAt: '',
  })

  const {
    loadingStocks,
    tabsState,
    listingStock,
    allListingStocks,
    getNextTabId,
    setTabsState,
    setListingStock,
    setAllListingStocks,
    makeEmptyFetchedStock,
    addListingTab,
    setActiveListingTab,
    renameListingTab,
    closeListingTab,
    unlinkListingTabFromLot,
  } = useListingTabsWithStocks()

  // persistent stock initialization (PT inventory):
  useEffect(() => {
    const zustandEmpty = !fetchedStock.stock.length && !fetchedStock.sealedStock.length

    const persistentHasData = persistentStock.fetchedAt !== ''

    if (!loadingPersistent && zustandEmpty && persistentHasData) {
      setFetchedStock(persistentStock)
    }
  }, [loadingPersistent, fetchedStock, persistentStock, setFetchedStock])

  useEffect(() => {
    setZoomLevel(INVENTORY_ZOOM_LEVELS.LOCATION)

    setSort({
      by: 'listedAt',
      direction: 'desc',
      type: 'date',
    })
  }, [setZoomLevel, setSort])

  useEffect(() => {
    if (user.featureFlags?.taxation) {
      // If the store is empty, I fetch the purchase events
      if (!purchaseEvents || purchaseEvents.length === 0) {
        fetchPurchaseEvents(1)
      }
    }
  }, [purchaseEvents?.length, fetchPurchaseEvents])

  useEffect(() => {
    if (user.featureFlags?.taxation) {
      // if I'm still loading stocks or lots -> I don't touch anything
      if (loadingStocks || loadingLots) return
      if (!purchaseEvents) return

      // set with all existing purchaseEvents ids
      const validLotIds = new Set(purchaseEvents.map((p) => p.id))

      // tabs that point to a lot that no longer exists
      const orphanTabIds: ListingTabId[] = tabsState.tabs
        .filter((tab) => tab.lotId && !validLotIds.has(tab.lotId))
        .map((tab) => tab.id as ListingTabId)

      // if there are no orphan tabs do nothing
      if (!orphanTabIds.length) return

      // 1) Tab metadata fix (remove lotId and adjust title)
      setTabsState((state) => {
        const fixedTabs = state.tabs.map((tab) => {
          if (tab.lotId && !validLotIds.has(tab.lotId)) {
            return {
              ...tab,
              lotId: undefined,
              title: `Tab ${tab.id}`,
            }
          }
          return tab
        })

        return { ...state, tabs: fixedTabs }
      })

      // 2) Fix stock in orphaned tabs:
      // remove purchase Event Id from items
      setAllListingStocks((prev) => {
        const next: Record<ListingTabId, FetchedStockInterface> = { ...prev }

        const cleanArticles = (arr: InventoryArticle[] = []): InventoryArticle[] =>
          arr.map((article) => {
            if (!article.purchaseEventId || validLotIds.has(article.purchaseEventId)) {
              return article
            }

            const copy: InventoryArticle = article.copy
              ? article.copy()
              : new InventoryArticle(article as any)

            copy.purchaseEventId = undefined
            return copy
          })

        orphanTabIds.forEach((tabId) => {
          const tabStock = next[tabId]
          if (!tabStock) return

          next[tabId] = {
            ...tabStock,
            stock: cleanArticles(tabStock.stock),
            sealedStock: cleanArticles(tabStock.sealedStock),
          }
        })

        return next
      })
    }
  }, [
    loadingStocks,
    loadingLots,
    purchaseEvents,
    tabsState.tabs,
    setTabsState,
    setAllListingStocks,
  ])

  /**
   * Problem:
   * articleRef.current keeps a live reference to the old article even when it's replaced.
   * React doesn't clear ref.current, so each modal opening can leave an object (with hundreds of properties) in memory.
   * Solution:
   * Explicitly clear the ref on dismount:
   */
  useEffect(() => {
    return () => {
      articleRef.current = null
    }
  }, [])

  useEffect(() => {
    const handler = () => setEditArticleModalOpen(false)
    window.addEventListener('pt:close-edit-article', handler)
    return () => window.removeEventListener('pt:close-edit-article', handler)
  }, [setEditArticleModalOpen])

  const setEditValue = useCallback((value: boolean) => setEditAfterAdding(value), [])
  const setIsNewArticle = useCallback((value: boolean) => setNewArticle(value), [])
  const articleRef = useRef<InventoryArticle | null>()
  const startEditingArticle = useCallback(
    (value: InventoryArticle, justAdded: boolean) => {
      if (articleToEdit) {
        enqueueSnackbar(t('addArticles.alreadyEditing'), { variant: 'error' })
        return false
      }
      setArticleToEdit(value)
      articleRef.current = value
      if (!justAdded) {
        setEditArticleModalOpen(true)
      }
      return true
    },
    [articleToEdit, articleRef]
  )

  const stopEditingArticle = useCallback(() => {
    setArticleToEdit(null)
    articleRef.current = null
  }, [])

  const editArticle = useCallback(
    (newArticle: InventoryArticle) => {
      setListingStock((s) => {
        const next = { ...s }
        const isSingles = activeStockType === STOCK_TYPES.SINGLES
        const prevArr = isSingles ? s.stock : s.sealedStock
        const idxEdited = prevArr.findIndex((a) => a.id === newArticle.id)
        if (idxEdited === -1) return next

        const old = prevArr[idxEdited]
        const stock = prevArr.slice()
        if (isSingles) newArticle.listedAt = old.listedAt
        stock[idxEdited] = newArticle

        // Propagate price changes to all articles matching the old one
        const sellPriceChanged = newArticle.price !== old.price
        const buyPriceChanged = newArticle.buyPrice !== old.buyPrice

        if (sellPriceChanged || buyPriceChanged) {
          for (let i = 0; i < stock.length; i++) {
            if (i === idxEdited) continue
            const a = stock[i]
            if (areArticlesMatching(old, a)) {
              const copy = a.copy()
              if (sellPriceChanged) copy.price = newArticle.price
              if (buyPriceChanged) {
                copy.buyPrice = newArticle.buyPrice
                copy.buyPriceLock = newArticle.buyPrice !== undefined ? true : undefined
              }
              stock[i] = copy
            }
          }
        }

        if (isSingles) next.stock = stock
        else next.sealedStock = stock

        return next
      })
    },
    [setListingStock, activeStockType]
  )

  const copyEditedArticle = useCallback(
    (newArticle) => {
      setListingStock((stockState) => ({
        ...stockState,
        stock: [...stockState.stock, newArticle],
      }))
    },
    [articleToEdit, setListingStock]
  )

  const determineTab = useCallback((article: InventoryArticle): ListingTab => {
    if (article.failedAutopricing) return 'failedAutopricing'
    else if (article.readyToPublish) return 'readyToPublish'
    else if (article.published) return 'published'
    else return 'articles'
  }, [])

  const handleArticlesPublished = (): void => {
    // Only the active stock type is sent to the dialog, so only that one may be drained:
    // selections left over from the other view must stay in the tab.
    const field: 'stock' | 'sealedStock' =
      activeStockType === STOCK_TYPES.SINGLES ? 'stock' : 'sealedStock'

    setListingStock((s) => ({
      ...s,
      [field]: s[field].filter((article: InventoryArticle) => !article.selected),
    }))
  }

  const handleSaveToInventoryDialogClose: React.Dispatch<React.SetStateAction<boolean>> = (
    action
  ) => {
    const next = typeof action === 'function' ? action(saveToInventoryDialogOpen) : action
    setSaveToInventoryDialogOpen(next)
    // When the user dismisses the success view, close the tab if it's a non-base tab and the
    // publish drained all its stock.
    if (
      !next &&
      listingStock.stock.length === 0 &&
      listingStock.sealedStock.length === 0 &&
      tabsState.activeTabId !== BASE_TAB_ID
    ) {
      closeListingTab(tabsState.activeTabId)
    }
  }

  const openSaveToInventoryDialog = useCallback(() => {
    setSaveToInventoryDialogOpen(true)
  }, [setSaveToInventoryDialogOpen])

  /**
   * Find or create the tab associated with a specific lot. It only creates a new object inside tabState and inside allListingStocks.
   */
  const findOrCreateLotTab = useCallback(
    (lot: PurchaseEvent): ListingTabId => {
      let lotTabId: ListingTabId | null = null

      setTabsState((state) => {
        // If there is already a tab linked to this batch, I reuse that one
        const existing = state.tabs.find((tab) => tab.lotId === lot.id)
        if (existing) {
          lotTabId = existing.id
          return state
        }

        // otherwise I create a new one with a new id, subsequent to the last existing one
        const nextId = getNextTabId(state.tabs)

        lotTabId = nextId

        const newTab: ListingTabMeta = {
          id: nextId,
          title: `${lot.lotNumber}`,
          lotId: lot.id,
        }

        return {
          ...state,
          tabs: [...state.tabs, newTab],
        }
      })

      // 2) Initialize empty stock for that tab (if it doesn't already exist)
      setAllListingStocks((prevStocks) => {
        if (lotTabId == null || Number.isNaN(lotTabId)) {
          console.error('findOrCreateLotTab: invalid lotTabId', lotTabId, { lot, prevStocks })
          return prevStocks
        }

        if (prevStocks[lotTabId]) return prevStocks

        return {
          ...prevStocks,
          [lotTabId]: makeEmptyFetchedStock(),
        }
      })

      //@ts-ignore
      return lotTabId as ListingTabId
    },
    [tabsState.tabs, setTabsState, setAllListingStocks, getNextTabId]
  )

  /**
   * Using Actions Presenter I'm moving a selection of items into a batch
   */
  const handleConfirmLinkToLotByListing = useCallback(
    (lot: PurchaseEvent) => {
      const sourceTabId = tabsState.activeTabId
      const targetTabId = findOrCreateLotTab(lot) // I create a new tab or insert everything into the existing tab

      // If something went wrong, don't touch the stocks
      if (Number.isNaN(targetTabId)) {
        console.error('handleConfirmLinkToLotByListing: targetTabId is NaN', {
          lot,
          sourceTabId,
          tabsState,
        })
        enqueueSnackbar(t('ptInventory.tabs.invalidTargetTab'), { variant: 'error' })
        return
      }

      setAllListingStocks((prev) => {
        const isSingles = activeStockType === STOCK_TYPES.SINGLES
        const field: 'stock' | 'sealedStock' = isSingles ? 'stock' : 'sealedStock'
        const stockUnchanged: 'stock' | 'sealedStock' = isSingles ? 'sealedStock' : 'stock'

        // CASO 1: I'm already in the lottery tab → I'm updating in place, no move
        if (sourceTabId === targetTabId) {
          const current = prev[sourceTabId] ?? makeEmptyFetchedStock()

          const updatedField = (current[field] || []).map((article) => {
            if (!article.selected) return article
            const copy = article.copy()
            copy.purchaseEventId = lot.id
            copy.selected = false
            return copy
          })

          return {
            ...prev,
            [sourceTabId]: {
              ...current,
              [field]: updatedField,
            },
          }
        }

        // CASO 2: I'm moving from tab A → tab lot B
        const sourceStock = prev[sourceTabId] ?? makeEmptyFetchedStock()
        const targetStock = prev[targetTabId] ?? makeEmptyFetchedStock()

        const fromArray = sourceStock[field] || []

        const keptInSource: InventoryArticle[] = []
        const movedToTarget: InventoryArticle[] = []

        fromArray.forEach((article) => {
          if (!article.selected) {
            keptInSource.push(article)
          } else {
            const copy = article.copy()
            copy.purchaseEventId = lot.id
            copy.selected = false
            movedToTarget.push(copy)
          }
        })

        if (
          !keptInSource.length &&
          !sourceStock[stockUnchanged].length &&
          sourceTabId !== BASE_TAB_ID
        ) {
          setTabsState((state) => {
            const remaining = state.tabs.filter((tab) => tab.id !== sourceTabId)
            return {
              tabs: remaining,
              activeTabId: targetTabId,
            }
          })
        }

        return {
          ...prev,
          [sourceTabId]: {
            ...sourceStock,
            [field]: keptInSource,
          },
          [targetTabId]: {
            ...targetStock,
            [field]: [...(targetStock[field] || []), ...movedToTarget],
          },
        }
      })
    },
    [tabsState.activeTabId, setAllListingStocks, activeStockType]
  )

  /**
   * I associate the entire tab and its contents to a batch
   */
  const associateListingTabToLot = useCallback(
    (tabId: ListingTabId, lot: PurchaseEvent) => {
      const tabs = tabsState.tabs
      const target = tabs.find((t) => t.id === tabId)

      if (!target) {
        enqueueSnackbar(t('ptInventory.tabs.targetTabNotFound'), { variant: 'error' })
        return
      }

      // I can't reassign a tab that's already associated with a different lot
      if (target.lotId !== undefined && target.lotId !== lot.id) {
        enqueueSnackbar(t('ptInventory.tabs.tabAlreadyLinkedToPe', { lotNumber: target.title }), {
          variant: 'warning',
        })
        return
      }

      const existingLotTab = tabs.find((t) => t.id !== tabId && t.lotId === lot.id)

      if (existingLotTab) {
        const targetTabId = existingLotTab.id

        // merge stocks: tabId -> targetTabId
        setAllListingStocks((prev) => {
          const source = prev[tabId] ?? makeEmptyFetchedStock()
          const targetStock = prev[targetTabId] ?? makeEmptyFetchedStock()

          const applyLot = (arr: InventoryArticle[] = []) =>
            arr.map((article) => {
              const copy = article.copy()
              copy.purchaseEventId = lot.id
              return copy
            })

          const merged: FetchedStockInterface = {
            ...targetStock,
            stock: applyLot([...(targetStock.stock || []), ...(source.stock || [])]),
            sealedStock: applyLot([
              ...(targetStock.sealedStock || []),
              ...(source.sealedStock || []),
            ]),
          }

          return {
            ...prev,
            [tabId]: makeEmptyFetchedStock(), // empty the source tab
            [targetTabId]: merged, // final tab with all the stock
          }
        })

        // Update the tab metadata:
        // - the existing tab remains associated with the lot
        // - the current tab becomes "free" again (Task N, without lotId)
        setTabsState((state) => ({
          ...state,
          tabs: state.tabs.map((tab) => {
            if (tab.id === tabId) {
              return {
                ...tab,
                lotId: undefined,
                title: `Tab ${tabId}`,
              }
            }
            if (tab.id === targetTabId) {
              return {
                ...tab,
                lotId: lot.id,
                title: `${lot.lotNumber}`,
              }
            }
            return tab
          }),
          activeTabId: targetTabId,
        }))

        enqueueSnackbar(t('ptInventory.tabs.peWithTab', { lotNumber: lot.lotNumber }), {
          variant: 'info',
        })

        return
      }

      const isTabZero = tabId === BASE_TAB_ID

      // CASO 1: tab NON base
      if (!isTabZero) {
        // update the stock of that tab
        setAllListingStocks((prev) => {
          const current = prev[tabId] ?? makeEmptyFetchedStock()

          const updateArray = (arr: InventoryArticle[] = []) =>
            arr.map((article) => {
              const copy = article.copy()
              copy.purchaseEventId = lot.id
              return copy
            })

          return {
            ...prev,
            [tabId]: {
              ...current,
              stock: updateArray(current.stock),
              sealedStock: updateArray(current.sealedStock),
            },
          }
        })

        // Update only the tab metadata
        setTabsState((state) => ({
          ...state,
          tabs: state.tabs.map((tab) =>
            tab.id === tabId
              ? {
                  ...tab,
                  lotId: lot.id,
                  title: `${lot.lotNumber}`,
                }
              : tab
          ),
        }))

        return
      }

      // CASE 2: I'm associating TAB ZERO

      // tab limit → snackbar outside the setter
      if (tabs.length >= MAX_LISTING_TABS) {
        enqueueSnackbar(t('ptInventory.tabs.tooManyTabs'), { variant: 'error' })
        return
      }

      // If I already have a tab associated with this lot, do not create another one
      const existingAssociated = tabs.find((t) => t.id !== BASE_TAB_ID && t.lotId === lot.id)
      if (existingAssociated) {
        setTabsState((state) => ({
          ...state,
          activeTabId: existingAssociated.id,
        }))
        return
      }

      const nextId = getNextTabId(tabs)

      //Protected stock migration: basic -> new associated tab
      setAllListingStocks((prevStocks) => {
        const baseStock = prevStocks[BASE_TAB_ID] ?? makeEmptyFetchedStock()

        const baseHasContent =
          (baseStock.stock?.length ?? 0) > 0 || (baseStock.sealedStock?.length ?? 0) > 0

        const targetStock = prevStocks[nextId]
        const targetHasContent =
          targetStock &&
          ((targetStock.stock?.length ?? 0) > 0 || (targetStock.sealedStock?.length ?? 0) > 0)

        // if the base is empty or the target tab already has content
        // (e.g., double call) → do nothing
        if (!baseHasContent || targetHasContent) {
          return prevStocks
        }

        const applyLot = (arr: InventoryArticle[] = []) =>
          arr.map((article) => {
            const copy = article.copy()
            copy.purchaseEventId = lot.id
            return copy
          })

        const migratedStock: FetchedStockInterface = {
          ...baseStock,
          stock: applyLot(baseStock.stock),
          sealedStock: applyLot(baseStock.sealedStock),
        }

        return {
          ...prevStocks,
          [BASE_TAB_ID]: makeEmptyFetchedStock(), // Tab Zero empty
          [nextId]: migratedStock, // new tab with content + lot info
        }
      })

      // I update the tab list: new base + associated tab
      setTabsState((state) => {
        const currentTabs = state.tabs
        const otherTabs = currentTabs.filter((t) => t.id !== BASE_TAB_ID)

        const newBaseTab: ListingTabMeta = {
          id: BASE_TAB_ID,
          title: `Tab ${BASE_TAB_ID}`,
          lotId: undefined,
        }

        const associatedTab: ListingTabMeta = {
          id: nextId,
          lotId: lot.id,
          title: `${lot.lotNumber}`,
        }

        return {
          ...state,
          tabs: [newBaseTab, ...otherTabs, associatedTab],
          activeTabId: nextId,
        }
      })
    },
    [tabsState, setTabsState, setAllListingStocks, makeEmptyFetchedStock]
  )

  const moveSelectedItemsToTab = useCallback(
    (targetTabId: ListingTabId) => {
      const sourceTabId = tabsState.activeTabId
      if (sourceTabId === targetTabId) {
        enqueueSnackbar(t('ptInventory.tabs.itemsAlreadyInTab'), { variant: 'warning' })
      }

      const isSingles = activeStockType === STOCK_TYPES.SINGLES
      const stockToUpdate: 'stock' | 'sealedStock' = isSingles ? 'stock' : 'sealedStock'

      setAllListingStocks((prev) => {
        const sourceStock = prev[sourceTabId] ?? makeEmptyFetchedStock()
        const targetStock = prev[targetTabId] ?? makeEmptyFetchedStock()

        const sourceLotId = tabsState.tabs.find((t) => t.id === sourceTabId)?.lotId
        const targetLotId = tabsState.tabs.find((t) => t.id === targetTabId)?.lotId

        const fromArray = sourceStock[stockToUpdate] || []

        const keptInSource: InventoryArticle[] = []
        const movedToTarget: InventoryArticle[] = [...(targetStock[stockToUpdate] || [])]

        fromArray.forEach((article) => {
          if (!article.selected) {
            keptInSource.push(article)
            return
          }

          const copy = article.copy()
          copy.selected = false

          if (targetLotId == null) {
            // target without batch -> I clean any dirty data
            copy.purchaseEventId = undefined
          } else if (targetLotId !== sourceLotId) {
            // target with different batch -> reconnect to that batch by calling the specialized function
            const lot = purchaseEvents.find((item) => item.id === targetLotId)
            handleConfirmLinkToLotByListing(lot!)
          }

          movedToTarget.push(copy)
        })

        return {
          ...prev,
          [sourceTabId]: {
            ...sourceStock,
            [stockToUpdate]: keptInSource,
          },
          [targetTabId]: {
            ...targetStock,
            [stockToUpdate]: movedToTarget,
          },
        }
      })

      setTabsState((state) => ({
        ...state,
        activeTabId: targetTabId,
      }))
    },
    [tabsState.activeTabId, setAllListingStocks, setTabsState, activeStockType]
  )

  const moveSelectedItemsToNewTab = useCallback(() => {
    let newTabId: ListingTabId | null = null

    setTabsState((state) => {
      if (state.tabs.length >= MAX_LISTING_TABS) return state
      const nextId = getNextTabId(state.tabs)
      newTabId = nextId as ListingTabId
      return {
        ...state,
        tabs: [...state.tabs, { id: nextId, title: `Tab ${nextId}` }],
      }
    })

    if (newTabId == null) return

    setAllListingStocks((prev) => ({
      ...prev,
      [newTabId!]: makeEmptyFetchedStock(),
    }))

    moveSelectedItemsToTab(newTabId)
  }, [
    setTabsState,
    setAllListingStocks,
    getNextTabId,
    makeEmptyFetchedStock,
    moveSelectedItemsToTab,
  ])

  const moveAllStockToTab = useCallback(
    (targetTabId: ListingTabId) => {
      const sourceTabId = tabsState.activeTabId
      if (sourceTabId === targetTabId) {
        enqueueSnackbar(t('ptInventory.tabs.itemsAlreadyInTab'), { variant: 'warning' })
      }

      setAllListingStocks((prev) => {
        const sourceStock = prev[sourceTabId] ?? makeEmptyFetchedStock()
        const targetStock = prev[targetTabId] ?? makeEmptyFetchedStock()

        const sourceLotId = tabsState.tabs.find((t) => t.id === sourceTabId)?.lotId
        const targetLotId = tabsState.tabs.find((t) => t.id === targetTabId)?.lotId

        const movedToTarget = { ...targetStock }

        if (targetLotId == null) {
          //target without batch -> I clean any dirty data
          sourceStock.stock.forEach((article) => {
            article.purchaseEventId = undefined
            article
          })
          sourceStock.sealedStock.forEach((article) => {
            article.purchaseEventId = undefined
          })
        } else if (targetLotId !== sourceLotId) {
          // target with different batch -> reconnect to that batch by calling the specialized function
          const lot = purchaseEvents.find((item) => item.id === targetLotId)
          sourceStock.stock.forEach((article) => {
            article.purchaseEventId = lot?.id
          })
          sourceStock.sealedStock.forEach((article) => {
            article.purchaseEventId = lot?.id
          })
        }

        movedToTarget.stock = movedToTarget.stock.concat(sourceStock.stock)
        movedToTarget.sealedStock = movedToTarget.sealedStock.concat(sourceStock.sealedStock)

        // if the tab is not base, then delete the tab because I moved everything
        if (sourceTabId !== BASE_TAB_ID) {
          setTabsState((state) => {
            const remaining = state.tabs.filter((tab) => tab.id !== sourceTabId)
            return {
              tabs: remaining,
              activeTabId: targetTabId,
            }
          })
        }

        if (sourceTabId === BASE_TAB_ID) {
          return {
            ...prev,
            [BASE_TAB_ID]: makeEmptyFetchedStock(),
            [targetTabId]: {
              ...movedToTarget,
            },
          }
        } else {
          return {
            ...prev,
            [targetTabId]: {
              ...movedToTarget,
            },
          }
        }
      })
    },
    [tabsState.activeTabId, setAllListingStocks, activeStockType]
  )

  const [run, setRun] = useState(user.enabledNotifications?.displayListingTour ?? false)
  const [stepIndex, setStepIndex] = useState(0)
  const { steps, runTour } = useListingTour({
    user,
    setUser,
    setRun,
    addListingTab,
    setStepIndex,
    listingStock,
  })

  return (
    <>
      <EditArticleContext.Provider
        value={{
          articleToEdit,
          startEditingArticle,
          stopEditingArticle,
          articleRef,
          editAfterAdding,
          setEditValue,
          newArticle,
          setIsNewArticle,
        }}
      >
        <CustomJoyrideTour
          steps={steps}
          run={run}
          stepIndex={stepIndex}
          callback={runTour}
          continuous
          showSkipButton
          spotlightClicks
          disableOverlayClose
          disableScrolling
          scrollToFirstStep={false}
          disableScrollParentFix
        />
        <InventoryPresenter<ListingTab>
          listingTabsState={tabsState}
          allListingStocks={allListingStocks}
          onAddListingTab={addListingTab}
          onActivateListingTab={setActiveListingTab}
          onRenameListingTab={renameListingTab}
          onCloseListingTab={closeListingTab}
          onAssociateListingTabToLot={associateListingTabToLot}
          unlinkListingTabFromLot={unlinkListingTabFromLot}
          moveSelectedItemsToTab={moveSelectedItemsToTab}
          moveSelectedItemsToNewTab={moveSelectedItemsToNewTab}
          moveAllStockToTab={moveAllStockToTab}
          purchaseEvents={purchaseEvents}
          determineTab={determineTab}
          allowAddingArticles
          emptyStockText={
            activeStockType === STOCK_TYPES.SINGLES
              ? t('stockPricing.noSingles')
              : t('stockPricing.noSealed')
          }
          stockState={listingStock}
          setStockState={setListingStock}
          openPublishDialog={openSaveToInventoryDialog}
          hideExportButton
          hideSettingsButton
          parentPage={LISTING_AND_APPRAISAL}
          handleConfirmLinkToLotByListing={handleConfirmLinkToLotByListing}
        />
        {articleToEdit && editArticleModalOpen && (
          <EditArticleModal
            disableHotkeys={false}
            template={articleToEdit}
            card={articleToEdit.card}
            editArticle={editArticle}
            copyEditedArticle={copyEditedArticle}
            open={editArticleModalOpen}
            setOpen={setEditArticleModalOpen}
            expansion={null}
          />
        )}
      </EditArticleContext.Provider>

      {/* save to PT inventory */}
      {saveToInventoryDialogOpen && (
        <SaveToInventoryDialog
          articles={
            activeStockType === 'singles'
              ? listingStock.stock.filter((article) => article.selected)
              : listingStock.sealedStock.filter((article) => article.selected)
          }
          onArticlesPublished={handleArticlesPublished}
          open={saveToInventoryDialogOpen}
          setOpen={handleSaveToInventoryDialogClose}
          tabsState={tabsState}
        />
      )}
    </>
  )
}
